import type { ReactNode } from "react";

import { PageHeader } from "@/components/shared/PageHeader";

/**
 * The page shell every request-detail surface renders through.
 *
 * Consumer, professional, certificate and admin request pages all show the
 * same thing — a header, some full-width summary cards, then documents beside
 * an activity timeline — but each one used to hand-assemble that shell. Ten
 * container divs across six files, five copies of the footer grid, and the
 * padding literal written fourteen times in three different variants.
 *
 * That divergence is invisible to `tsc` and to the build. It surfaces only
 * when two pages are screenshotted side by side, which is exactly how the RFQ
 * consumer detail reached a user with three simultaneous regressions
 * (oversized card titles, airy two-column rows, a full-bleed stack instead of
 * the footer grid) and how the admin RFQ page still renders inside a stray
 * `max-w-3xl`.
 *
 * So: fill the slots. Do not rebuild the shell.
 *
 * There is deliberately NO `className` prop on the container. An escape hatch
 * is how a shared layout decays back into per-page styling — the first
 * `max-w-3xl` passed in re-creates the precise bug this removes. A surface
 * that genuinely cannot fit should compose `PageHeader` and `DetailRows`
 * directly and be a visible exception rather than a quiet override.
 */
export interface RequestDetailLayoutProps {
  title: ReactNode;
  description?: ReactNode;
  onBack?: () => void;
  /** Header-right actions, e.g. a cancel button. */
  actions?: ReactNode;
  /**
   * Rendered between the header and the body — the profile-completion gate
   * banner is the current caller.
   */
  banner?: ReactNode;
  /** Full-width cards: summary, quotes to review, child-request link. */
  children: ReactNode;
  /**
   * First footer column — documents or attachments.
   *
   * The footer is a two-column grid on `lg` and a single stack below it, so
   * these slots are ORDER-based, not position-based: `primary` comes first in
   * both. Passing only one slot is fine; it takes the first cell rather than
   * leaving a hole.
   */
  primary?: ReactNode;
  /** Second footer column — the activity timeline. See `primary`. */
  secondary?: ReactNode;
  /**
   * Rendered after the footer, outside the grid. For dialogs and other
   * non-visual nodes a page needs to mount somewhere.
   */
  overlays?: ReactNode;
}

/**
 * Whether a slot will actually paint something.
 *
 * `null`/`undefined` are the obvious empties, but the common React idiom
 * `condition && <Card />` evaluates to `false`, not null — a `!= null` test
 * calls that "present" and the footer then renders an empty half-width cell.
 */
function isRenderable(slot: ReactNode): boolean {
  return slot != null && slot !== false && slot !== "";
}

export function RequestDetailLayout({
  title,
  description,
  onBack,
  actions,
  banner,
  children,
  primary,
  secondary,
  overlays,
}: RequestDetailLayoutProps): JSX.Element {
  const hasFooter = isRenderable(primary) || isRenderable(secondary);
  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={title}
        description={description}
        onBack={onBack}
        actions={actions}
      />
      {banner}
      <div className="mt-6 space-y-6">
        {children}
        {hasFooter ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
            {primary}
            {secondary}
          </div>
        ) : null}
      </div>
      {overlays}
    </div>
  );
}
